import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { CartItem, Product } from '../types/product';
import { API_CONFIG } from '../services/config';

interface CartContextValue {
  items: CartItem[];
  drawerOpen: boolean;
  toastItem: string | null;
  addItem: (product: Product) => void;
  removeItem: (id: string | number) => void;
  updateQuantity: (id: string | number, quantity: number) => void;
  clearCart: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  totalItems: number;
  totalPrice: number;
  checkoutViaWhatsApp: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toastItem, setToastItem] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addItem = useCallback((product: Product) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    // Show toast but don't auto-open drawer
    if (toastTimer.current) clearTimeout(toastTimer.current);
    const shortName = product.name.length > 32 ? product.name.slice(0, 32) + '…' : product.name;
    setToastItem(shortName);
    toastTimer.current = setTimeout(() => setToastItem(null), 2800);
  }, []);

  const removeItem = useCallback((id: string | number) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string | number, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity } : i)));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + (i.price ?? 0) * i.quantity, 0);

  const checkoutViaWhatsApp = useCallback(() => {
    if (items.length === 0) return;
    const lines = items
      .map(
        (i) =>
          `• ${i.name} x${i.quantity}${
            i.price
              ? ` — RD$${(i.price * i.quantity).toLocaleString('es-DO')}`
              : ''
          }`
      )
      .join('\n');
    const totalStr =
      totalPrice > 0
        ? `\n\n💰 *Total estimado:* RD$${totalPrice.toLocaleString('es-DO')}`
        : '';
    const msg = `¡Hola! Quiero realizar el siguiente pedido en SpartechRDRD:\n\n🛒 *Mi carrito:*\n${lines}${totalStr}\n\nPor favor, ¿me pueden ayudar a finalizar mi compra?`;
    window.open(
      `https://wa.me/${API_CONFIG.WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`,
      '_blank'
    );
  }, [items, totalPrice]);

  return (
    <CartContext.Provider
      value={{
        items,
        drawerOpen,
        toastItem,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        openDrawer,
        closeDrawer,
        totalItems,
        totalPrice,
        checkoutViaWhatsApp,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
